import type { FC } from "react";
import { useEffect, useState } from "react";

import type { IButtonsBarV2 } from "@/design-system/buttons-bar";
import TelemetryButton from "@/design-system/telemetry-button";
import { useProcessId } from "@/lib/hooks/use-process-id";
import { GoToShopButtonResources } from "@/src/cashback/GoToShopButton.Resources";
import { GoToShopButtonResourcesEn } from "@/src/cashback/GoToShopButton.Resources.En";
import RegisterNow from "@/src/common/RegisterNow";
import { languageSelector, useUserAuthenticationContext } from "@/src/contexts";
import { type PropsTelemetryCustom, sendToGTMEventClick, urlFormat } from "@Components/Modules/Helpers";
import { BrandLink } from "@Routes/ClickLinkLinks";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type PropsWithStyleX, type QueryXsMax, colors } from "../../styles/tokens.stylex";
import type { IAdBlockModal } from "../browser-support/AdBlockModal";
import AdBlockModal from "../browser-support/AdBlockModal";
import type { IRulesModal } from "./RulesModal";
import { RulesModal } from "./RulesModal";

export interface IGoToShopButton extends PropsTelemetryCustom {
    adBlocker: IAdBlockModal;
    isAuthenticated: boolean;
    rulesModal: IRulesModal;
    brandId: string;
    discountId?: string;
    brandName: string;
    callGTM?: () => void;
    buttonStyleX?: PropsWithStyleX["styleX"];
}

interface IGoToShopButtonState {
    openNewRegister: boolean;
    showRules: boolean;
    isBlocked: boolean;
}

export const GoToShopButton: FC<IGoToShopButton> = (props) => {
    const { isAuthenticated } = useUserAuthenticationContext();
    const [state, setState] = useState<IGoToShopButtonState>({
        openNewRegister: false,
        showRules: false,
        isBlocked: false,
    });

    const { get, set, remove } = useProcessId();

    const resources = languageSelector(GoToShopButtonResources, GoToShopButtonResourcesEn);

    useEffect(() => {
        const openProcessIdKey = get();
        if (openProcessIdKey && props.brandId === openProcessIdKey) {
            if (!isAuthenticated) {
                setState((s) => ({ ...s, openNewRegister: true }));
            }
            remove();
        }

        fetch("/adsbygoogle.js?=adv/GetAdvertisment")
            .then(() => setState((s) => ({ ...s, isBlocked: false })))
            .catch(() => setState((s) => ({ ...s, isBlocked: true })));
    }, []);

    const onRulesClose = () => setState((currState) => ({ ...currState, showRules: false }));

    const onButtonClick = () => {
        setState((currState) => ({
            ...currState,
            openNewRegister: !isAuthenticated,
            showRules: isAuthenticated,
        }));

        if (props.callGTM) {
            props.callGTM();
        }

        return !!props.telemetryCustom;
    };

    const navigateUrl = urlFormat(BrandLink, { brandId: props.brandId, discountId: props.discountId });

    const renderCashbackRegisterNow = () => {
        if (!state.openNewRegister) {
            return null;
        }

        const closeNewRegisterModal = () => {
            setState((currState) => ({ ...currState, openNewRegister: false }));
            remove();
        };

        const onRegistrationClick = () => {
            set(props.brandId);
            setState((s) => ({ ...s, openNewRegister: false }));
        };

        return (
            <RegisterNow
                clickLink={navigateUrl}
                onCancelClose={closeNewRegisterModal}
                onRegistrationClick={onRegistrationClick}
            />
        );
    };

    const rulesModalBtnBar: IButtonsBarV2 = {
        cancelButton: {
            as: "button",
            variant: "secondary",
            size: "M",
            styleX: [styles.button, styles.cancelButton],
            eventName: "common-back",
            onClick: onRulesClose,
        },
        nextButton: {
            id: "activate-cashback",
            as: "a",
            href: navigateUrl,
            variant: "magentaRound",
            onClick: () => {
                if (typeof window.postMessage !== "undefined") {
                    window.postMessage(
                        {
                            action: "ACTIVATE_CASHBACK",
                            data: {
                                brandId: props.brandId,
                            },
                        },
                        `https://${window.location.hostname}`,
                    );
                }
                sendToGTMEventClick("cshbck_outboundInfoGoToStore");
                onRulesClose();
            },
            target: "_blank",
            //@ts-expect-error Keeping the data brand id attribute to not break functionality with migration
            "data-brand-id": props.brandId,
            eventName: "cshbck-outboundInfoGoToStore",
            itemId: props.brandId,
            styleX: styles.button,
        },
        styleX: styles.buttonsBar,
    };

    return (
        <>
            <TelemetryButton
                as="button"
                variant="magentaRound"
                size="M"
                onClick={onButtonClick}
                type="button"
                styleX={[styles.goToStoreButton, props.buttonStyleX]}
                aria-label={`${resources.cashbackButtonText} ${props.brandName}`}
            >
                {resources.cashbackButtonText}
            </TelemetryButton>

            {renderCashbackRegisterNow()}
            {state.showRules &&
                (state.isBlocked ? (
                    <AdBlockModal {...props.adBlocker} onClose={onRulesClose} />
                ) : (
                    <RulesModal
                        {...props.rulesModal}
                        brandId={props.brandId}
                        discountId={props.discountId || props.rulesModal.discountId}
                        buttonsBar={rulesModalBtnBar}
                        onClose={onRulesClose}
                        trackingActive
                    />
                ))}
        </>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    buttonsBar: {
        backgroundColor: colors.bg_BrandPrimary,
        flexDirection: { default: "row", [xsMax]: "column" },
        justifyContent: "space-around",
        rowGap: 20,
    },

    button: {
        minWidth: 120,
        width: { default: "40%", [xsMax]: "100%" },
    },

    cancelButton: {
        boxShadow: { default: null, ":hover": `0 0 .3em ${colors.fg_Accent}` },
        color: colors.fg_Accent,
        order: { default: 0, [xsMax]: 1 },
        outlineColor: colors.fg_AltDefault,
    },

    goToStoreButton: {
        borderRadius: ComposableSpacing.s2,
        width: "100%",
    },
});
